import React from 'react';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import CopyButton from './CopyButton';
import AmazonCaseForm from './AmazonCaseForm';
import PhotoGrid from './PhotoGrid';
import CaseComments from './CaseComments';
import { DISPOSITION_OPTIONS, getStatusColor, formatDate } from './constants';

// Right-hand master-detail panel for the selected case.
export default function CaseDetailPanel({
    selectedCase,
    loadingDetails,
    emailTemplates,
    onStatusChange,
    onRefresh,
    onOpenUpload
}) {
    if (!selectedCase && loadingDetails) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <svg className="animate-spin h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading case...
            </div>
        );
    }

    if (!selectedCase) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="font-medium">Select a case to review</p>
            </div>
        );
    }

    const colors = getStatusColor(selectedCase.status);
    const d = selectedCase.parsedData || {};

    const productFields = [
        { key: 'item_name', label: 'Item Name', mono: false },
        { key: 'asin1', label: 'ASIN', mono: true },
        { key: 'fnsku', label: 'FNSKU', mono: true },
        { key: 'qty', label: 'Quantity', mono: false },
        { key: 'count', label: 'Count', mono: false },
        { key: 'shipped_quantity', label: 'Shipped Qty', mono: false },
        { key: 'disposition', label: 'Disposition', mono: false },
        { key: 'order_id', label: 'Removal ID', mono: true },
        { key: 'searchquery', label: 'Tracking #', mono: false }
    ];
    const productEntries = productFields
        .filter((f) => d[f.key])
        .map((f) => ({ ...f, value: String(d[f.key]) }));

    // Unit cost is joined server-side from the vendor price list, so it lives on
    // the case row rather than inside the qr_case_data JSON blob.
    const unitCost = selectedCase.unit_cost;
    if (unitCost !== null && unitCost !== undefined && unitCost !== '') {
        productEntries.push({
            key: 'unit_cost',
            label: 'Unit Cost',
            mono: false,
            value: `$${Number(unitCost).toFixed(2)}`
        });
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-mono font-bold text-xl text-gray-900">
                        {selectedCase.qr_case_id}
                        {d.amazon_case_id && <span className="text-blue-600"> · Amazon Case {d.amazon_case_id}</span>}
                    </div>
                    <span className={`mt-1 inline-block px-3 py-1 rounded-lg font-bold text-sm ${colors.bg} ${colors.text}`}>
                        {selectedCase.status}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onOpenUpload}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 whitespace-nowrap"
                >
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    Upload Photos
                </button>
            </div>

            {/* Case info */}
            <div>
                <h3 className="font-semibold mb-2">Case Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-600">Quantity:</span><span className="ml-2 font-medium">{selectedCase.quantity || 0}</span></div>
                    <div><span className="text-gray-600">Photos:</span><span className="ml-2 font-medium">{selectedCase.photos?.length || 0}</span></div>
                    <div className="col-span-2"><span className="text-gray-600">Created:</span><span className="ml-2 font-medium">{formatDate(selectedCase.created_at)}</span></div>
                    <div className="col-span-2"><span className="text-gray-600">Updated:</span><span className="ml-2 font-medium">{formatDate(selectedCase.updated_at)}</span></div>
                </div>
                {selectedCase.problem_description && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <span className="text-gray-800 font-bold block mb-2">Problem Description:</span>
                        <p className="text-gray-800 leading-relaxed">{selectedCase.problem_description}</p>
                    </div>
                )}
            </div>

            {/* Product data */}
            {productEntries.length > 0 && (
                <div>
                    <h3 className="font-semibold mb-2">Product Data</h3>
                    <div className="text-base bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                            {productEntries.map((f) => (
                                <div key={f.key} className="flex items-start justify-between gap-2">
                                    <div>
                                        <span className="text-gray-700 font-bold">{f.label}:</span>
                                        <span className={`ml-2 ${f.mono ? 'font-mono' : ''}`}>{f.value}</span>
                                    </div>
                                    <CopyButton value={f.value} label={f.label} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Status */}
            <div>
                <h3 className="font-semibold mb-2">Case Status</h3>
                <select
                    value={selectedCase.status || 'Pending Case Creation'}
                    onChange={(e) => onStatusChange(selectedCase.qr_case_id, e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    {DISPOSITION_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
            </div>

            <AmazonCaseForm selectedCase={selectedCase} emailTemplates={emailTemplates} onSaved={onRefresh} />

            <div className="border-t pt-6">
                <PhotoGrid caseId={selectedCase.qr_case_id} photos={selectedCase.photos} />
            </div>

            <CaseComments caseId={selectedCase.qr_case_id} />
        </div>
    );
}
